import type {
  WordCollection,
  UserSubscriptionDetails,
  UserStats,
  UserInteraction,
  ProfileUser,
  UpdateUser,
  Word,
} from '@lingugeek/schemas';

// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

// Authentication State
export interface AuthState {
  user: ProfileUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | Error | unknown | null;
}

// Subscription State
export interface SubscriptionState {
  subscription: UserSubscriptionDetails | null;
  isLoading: boolean;
  error: string | Error | unknown | null;
}

// Learning State
export interface LearningState {
  todaysWords: Word[];
  currentSessionToken: string | null;
  currentWordIndex: number;
  completedWordsIds: string[];
  isSessionActive: boolean;
  sessionExpiresAt: string | null;
  isLoading: boolean;
  error: string | Error | unknown | null;
}

// User Stats State
export interface UserStatsState {
  stats: UserStats | null;
  interactions: UserInteraction[];
  progress: UserStats | null;
  isLoading: boolean;
  error: string | Error | unknown | null;
}

// Words State
export interface WordsState {
  words: Word[];
  collections: {
    history: Pick<WordCollection, 'id' | 'date'>[];
    current: WordCollection | null;
  };
  isLoading: boolean;
  error: string | Error | unknown | null;
}

// Global Store State
export interface GlobalState {
  auth: AuthState;
  subscription: SubscriptionState;
  learning: LearningState;
  userStats: UserStatsState;
  words: WordsState;
}

// Action Types
export interface AuthActions {
  login: (user: ProfileUser) => void;
  logout: () => Promise<void>;
  updateUser: (updates: UpdateUser) => Promise<void>;
  refreshUser: () => Promise<void>;
  googleLogin: (accessToken: string) => Promise<{
    token: string;
    user: { id: string; name: string; email: string; picture?: string };
  }>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | Error | unknown | null) => void;
}

export interface SubscriptionActions {
  loadSubscription: () => Promise<void>;
  upgradeSubscription: (plan: 'pro' | 'ultra') => Promise<void>;
  downgradeSubscription: (plan: 'free' | 'pro') => Promise<void>;
  cancelSubscription: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | Error | unknown | null) => void;
  setSubscription: (subscription: any) => void;
  updateSubscription: (subscription: any) => void;
}

export interface LearningActions {
  setSessionToken: (token: string | null) => void;
  loadTodaysWords: (token: string) => Promise<void>;
  startSession: (token: string) => Promise<void>;
  markWordAsLearned: (token: string, wordId: string) => Promise<void>;
  expireSession: (token: string) => Promise<void>;
  setCurrentWordIndex: (index: number) => void;
  resetSession: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | Error | unknown | null) => void;
}

export interface UserStatsActions {
  loadStats: () => Promise<void>;
  loadInteractions: (
    limit?: number,
    options?: { journeyOnly?: boolean }
  ) => Promise<void>;
  loadProgress: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | Error | unknown | null) => void;
}

export interface WordsActions {
  loadWords: (query?: string) => Promise<void>;
  loadCollectionHistory: () => Promise<void>;
  loadCollection: (id: string) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | Error | unknown | null) => void;
}

// Combined store type
export interface Store extends GlobalState {
  actions: {
    auth: AuthActions;
    subscription: SubscriptionActions;
    learning: LearningActions;
    userStats: UserStatsActions;
    words: WordsActions;
  };
}

// Query keys for React Query
export const QUERY_KEYS = {
  user: ['user'] as const,
  subscription: ['subscription'] as const,
  stats: ['stats'] as const,
  interactions: (limit?: number, journeyOnly?: boolean) =>
    ['interactions', limit, journeyOnly] as const,
  progress: ['progress'] as const,
  words: (query?: string) => ['words', query] as const,
  todaysWords: (token: string) => ['todaysWords', token] as const,
  collectionHistory: ['collectionHistory'] as const,
  collection: (id: string) => ['collection', id] as const,
} as const;

// Configuration types
export interface StateManagerConfig {
  apiBaseUrl: string;
  queryDefaults?: {
    staleTime?: number;
    cacheTime?: number;
    refetchOnWindowFocus?: boolean;
    retry?: number | boolean;
  };
  auth?: {
    tokenRefreshThreshold?: number;
    maxRetries?: number;
  };
}
